"use client";

import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchAllCards,
  fetchCardById,
  createCardByPatient,
  createCardByDoctor,
  updateCard,
  deleteCard,
  fetchStatistics,
  fetchGlobalStats,
  fetchUnverifiedCards,
  fetchExpiringCards,
  fetchExpiredCards,
  verifyCardByIdentifiant,
  searchByName,
  searchByCin,
  fetchCardsByPatient,
  fetchDependentsByPatient,
  fetchCardsByCreator,
  addDependent,
  fetchDependentsByCard,
  fetchDependentById,
  updateDependent,
  deleteDependent,
  createMedicalAct,
  fetchActsByPatient,
  deleteMedicalAct,
  fetchCardsByDoctorPatient,
} from "../redux/thunks/cnssThunks";

import {
  selectAllCards,
  selectCurrentCard,
  selectUnverifiedCards,
  selectExpiringCards,
  selectExpiredCards,
  selectSearchResults,
  selectVerificationResult,
  selectDependents,
  selectCurrentDependent,
  selectAllDependents,
  selectMedicalActs,
  selectConsultations,
  selectActesMedicaux,
  selectAccouchements,
  selectTotalHonoraires,
  selectStatistics,
  selectGlobalStats,
  selectIsLoadingCards,
  selectIsLoadingCard,
  selectIsCreating,
  selectIsUpdating,
  selectIsDeleting,
  selectIsLoadingMedicalActs,
  selectIsCreatingAct,
  selectIsDeletingAct,
  selectIsLoadingDependents,
  selectIsAddingDependent,
  selectIsSearching,
  selectIsVerifying,
  selectCnssError,
  selectCnssSuccess,
  selectDoctorPatientCards,
  selectCurrentDoctorPatientCard,
  selectIsLoadingDoctorPatientCards,
} from "../redux/selectors/cnssSelectors";

import {
  clearError,
  clearSuccess,
  clearCurrentCard,
  clearCurrentDependent,
  clearVerification,
  clearSearchResults,
  clearMedicalActs,
  resetCnssState,
} from "../redux/slices/cnssSlice";

/* ================= CARTES CNSS ================= */

export function useCnssCards() {
  const dispatch = useDispatch();

  return {
    cards:           useSelector(selectAllCards)          ?? [],
    currentCard:     useSelector(selectCurrentCard)       ?? null,
    unverifiedCards: useSelector(selectUnverifiedCards)   ?? [],
    expiringCards:   useSelector(selectExpiringCards)     ?? [],
    expiredCards:    useSelector(selectExpiredCards)      ?? [],
    isLoadingCards:  useSelector(selectIsLoadingCards)    ?? false,
    isLoadingCard:   useSelector(selectIsLoadingCard)     ?? false,
    isCreating:      useSelector(selectIsCreating)        ?? false,
    isUpdating:      useSelector(selectIsUpdating)        ?? false,
    isDeleting:      useSelector(selectIsDeleting)        ?? false,
    error:           useSelector(selectCnssError)         ?? null,
    success:         useSelector(selectCnssSuccess)       ?? null,
    doctorPatientCards:
  useSelector(selectDoctorPatientCards) ?? [],

currentDoctorPatientCard:
  useSelector(selectCurrentDoctorPatientCard) ?? null,

isLoadingDoctorPatientCards:
  useSelector(selectIsLoadingDoctorPatientCards) ?? false,

fetchByDoctorPatient: useCallback(
  (doctorId, patientId) => {
    return dispatch(fetchCardsByDoctorPatient({ doctorId, patientId }));
  },
  [dispatch]
),

    fetchAll: useCallback(
      () => dispatch(fetchAllCards()),
      [dispatch]
    ),
    fetchById: useCallback(
      (id) => {
        return dispatch(fetchCardById(id));
      },
      [dispatch]
    ),
    fetchByPatient: useCallback(
      (patientId) => {
        return dispatch(fetchCardsByPatient(patientId));
      },
      [dispatch]
    ),
    fetchByCreator: useCallback(
      (createdBy) => dispatch(fetchCardsByCreator(createdBy)),
      [dispatch]
    ),
    fetchUnverified: useCallback(
      () => dispatch(fetchUnverifiedCards()),
      [dispatch]
    ),
    fetchExpiring: useCallback(
      () => dispatch(fetchExpiringCards()),
      [dispatch]
    ),
    fetchExpired: useCallback(
      () => dispatch(fetchExpiredCards()),
      [dispatch]
    ),

    createByPatient: useCallback(
      (userId, cardData) => {
        return dispatch(createCardByPatient({ userId, cardData }));
      },
      [dispatch]
    ),
    createByDoctor: useCallback(
      (doctorId, patientId, cardData) => {
        return dispatch(createCardByDoctor({ doctorId, patientId, cardData }));
      },
      [dispatch]
    ),
    update: useCallback(
      (cardId, verifierId, updateData) => {
        return dispatch(updateCard({ cardId, verifierId, updateData }));
      },
      [dispatch]
    ),
    remove: useCallback(
      (cardId) => dispatch(deleteCard(cardId)),
      [dispatch]
    ),

    clearCurrent: useCallback(() => dispatch(clearCurrentCard()),  [dispatch]),
    clearErr:     useCallback(() => dispatch(clearError()),        [dispatch]),
    clearOk:      useCallback(() => dispatch(clearSuccess()),      [dispatch]),
    reset:        useCallback(() => dispatch(resetCnssState()),    [dispatch]),
  };

  
}

/* ================= RECHERCHE ================= */

export function useCnssSearch() {
  const dispatch = useDispatch();

  return {
    searchResults:      useSelector(selectSearchResults)      ?? [],
    verificationResult: useSelector(selectVerificationResult) ?? null,
    isSearching:        useSelector(selectIsSearching)        ?? false,
    isVerifying:        useSelector(selectIsVerifying)        ?? false,
    error:              useSelector(selectCnssError)          ?? null,

    searchByName: useCallback(
      (nomPrenom) => dispatch(searchByName(nomPrenom)),
      [dispatch]
    ),
    searchByCin: useCallback(
      (cin) => dispatch(searchByCin(cin)),
      [dispatch]
    ),
    verify: useCallback(
      (identifiant) => {
        return dispatch(verifyCardByIdentifiant(identifiant));
      },
      [dispatch]
    ),
    clearSearch: useCallback(() => dispatch(clearSearchResults()), [dispatch]),
    clearVerify: useCallback(() => dispatch(clearVerification()),  [dispatch]),
  };
}

/* ================= DÉPENDANTS ================= */

export function useCnssDependents() {
  const dispatch = useDispatch();

  return {
    dependents:       useSelector(selectDependents)          ?? [],
    currentDependent: useSelector(selectCurrentDependent)    ?? null,
    allDependents:    useSelector(selectAllDependents)       ?? [],
    isLoading:        useSelector(selectIsLoadingDependents) ?? false,
    isAdding:         useSelector(selectIsAddingDependent)   ?? false,
    error:            useSelector(selectCnssError)           ?? null,
    success:          useSelector(selectCnssSuccess)         ?? null,

    fetchByCard: useCallback(
      (cardId, userId) => {
        return dispatch(fetchDependentsByCard({ cardId, userId }));
      },
      [dispatch]
    ),
    fetchById: useCallback(
      (cardId, userId, dependentId) =>
        dispatch(fetchDependentById({ cardId, userId, dependentId })),
      [dispatch]
    ),
    fetchByPatient: useCallback(
      (patientId) => dispatch(fetchDependentsByPatient(patientId)),
      [dispatch]
    ),

    add: useCallback(
      (cardId, userId, dependentData) => {
        return dispatch(addDependent({ cardId, userId, dependentData }));
      },
      [dispatch]
    ),
    update: useCallback(
      (cardId, userId, dependentId, updateData) => {
        return dispatch(updateDependent({ cardId, userId, dependentId, updateData }));
      },
      [dispatch]
    ),
    remove: useCallback(
      (cardId, userId, dependentId) => {
        return dispatch(deleteDependent({ cardId, userId, dependentId }));
      },
      [dispatch]
    ),

    clearCurrent: useCallback(() => dispatch(clearCurrentDependent()), [dispatch]),
    clearErr:     useCallback(() => dispatch(clearError()),            [dispatch]),
    clearOk:      useCallback(() => dispatch(clearSuccess()),          [dispatch]),
  };
}

/* ================= ACTES MÉDICAUX (BS) ================= */

export function useMedicalActs() {
  const dispatch = useDispatch();

  const medicalActs     = useSelector(selectMedicalActs)         ?? [];
  const consultations   = useSelector(selectConsultations)       ?? [];
  const actesMedicaux   = useSelector(selectActesMedicaux)       ?? [];
  const accouchements   = useSelector(selectAccouchements)       ?? [];
  const totalHonoraires = useSelector(selectTotalHonoraires)     ?? 0;
  const isLoading       = useSelector(selectIsLoadingMedicalActs) ?? false;
  const isCreating      = useSelector(selectIsCreatingAct)       ?? false;
  const isDeleting      = useSelector(selectIsDeletingAct)       ?? false;
  const error           = useSelector(selectCnssError)           ?? null;
  const success         = useSelector(selectCnssSuccess)         ?? null;

  const fetchActs = useCallback(
    (patientId, type = null) => {
      return dispatch(fetchActsByPatient({ patientId, type }));
    },
    [dispatch]
  );

  const createAct = useCallback(
    (doctorId, patientId, actData) => {
      return dispatch(createMedicalAct({ doctorId, patientId, actData }));
    },
    [dispatch]
  );

  const deleteAct = useCallback(
    (actId) => dispatch(deleteMedicalAct(actId)),
    [dispatch]
  );

  const clearActs = useCallback(
    () => dispatch(clearMedicalActs()),
    [dispatch]
  );

  const clearErr = useCallback(
    () => dispatch(clearError()),
    [dispatch]
  );

  const clearOk = useCallback(
    () => dispatch(clearSuccess()),
    [dispatch]
  );

  return {
    medicalActs,
    consultations,
    actesMedicaux,
    accouchements,
    totalHonoraires,
    isLoading,
    isCreating,
    isDeleting,
    error,
    success,
    fetchActs,
    createAct,
    deleteAct,
    clearActs,
    clearErr,
    clearOk,
  };
}

/* ================= STATISTIQUES ================= */

export function useCnssStats() {
  const dispatch = useDispatch();

  return {
    statistics:  useSelector(selectStatistics)  ?? null,
    globalStats: useSelector(selectGlobalStats) ?? null,
    isLoading:   useSelector((s) => s.cnss?.loading?.statistics ?? false),
    error:       useSelector(selectCnssError)   ?? null,

    fetchStats:       useCallback(() => dispatch(fetchStatistics()),  [dispatch]),
    fetchGlobalStats: useCallback(() => dispatch(fetchGlobalStats()), [dispatch]),
  };
}